import React, { useState } from 'react';
import { DialogClose } from '@/components/atoms/Dialog';
import { Button } from '@/components/atoms/NewPcButton';
import { useToast } from '@/hooks/V3/useToast';
import { useAppSelector } from '@/redux/store';
import { CPLAT_API_ROUTES, usePost } from '@/api';
import { ROUTES } from '@/routes';
import {
  createMembershipPaymentResultPath,
  getPaymentResultIdFromResponse,
} from '@/utils/membership-payment-result';
import {
  createPendingSubscriptionPayload,
  getRegisterPendingSubscriptionErrorMessage,
  WEB_PC_MEMBERSHIP_CHANNEL,
  type MembershipChannel,
} from '@/utils/pending-subscription';
import { useNavigate } from 'react-router-dom';
import type { MembershipPaymentDialogMode } from '@/containers/payment/NewPaymentDialog';
import { getCplatCode, getCplatMessage } from '@/pages/mobile-membership/mobile-membership-api';

// {
//   "cplatToken": "string",
//   "subscriptionNo": 0,
//   "cardNo": "string",
//   "expirationDate": "string",
//   "password": "string",
//   "cardOwnerInfo": "string",
//   "promotionCode": "string",
//   "alreadyPaidInfo": {
//     "appleTransactionId": "string",
//     "appleResponseJson": "string",
//     "googleTransactionId": "string",
//     "googleResponseJson": "string"
//   }
// }

interface ChangeBillCardProps {
  isPrePayment?: boolean;
  subscriptionNo: number;
  paymentAmount: number;
  setBillDialogOpen: (open: boolean) => void;
  paymentChange?: boolean;
  mode?: MembershipPaymentDialogMode;
  pendingSubscriptionChannel?: MembershipChannel;
  onPendingSubscriptionSuccess?: () => void;
}

const CARD_INPUT_CLASS =
  'box-border w-full rounded-[8px] border border-gr-300 px-[10px] py-[12px] text-gr-700 Body5M14 outline-none transition-colors focus:border-point-500 focus:ring-1 focus:ring-inset focus:ring-point-500';
const MIN_CARD_NUMBER_LENGTH = 13;
const MAX_CARD_NUMBER_LENGTH = 19;
const MAX_FORMATTED_CARD_NUMBER_LENGTH = 23;

const ChangeBillCard = ({
  isPrePayment,
  subscriptionNo,
  paymentAmount,
  setBillDialogOpen,
  paymentChange = false,
  mode = 'subscription',
  pendingSubscriptionChannel,
  onPendingSubscriptionSuccess,
}: ChangeBillCardProps) => {
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardPassword, setCardPassword] = useState('');
  const [cardBirth, setCardBirth] = useState('');
  const { showToast } = useToast();
  const { CplatToken } = useAppSelector((state) => state.user);
  const navigate = useNavigate();

  // 카드 결제(구독 등록) API
  const { mutate: requestCardPay, isPending: isLoading } = usePost(
    CPLAT_API_ROUTES.setSubscription,
  );
  const { mutate: registerCardForBilling, isPending: isRegisterCardPending } = usePost(
    CPLAT_API_ROUTES.registerCardForBilling,
  );
  const { mutate: requestPendingSubscription, isPending: isRegisterPendingSubscriptionPending } =
    usePost(CPLAT_API_ROUTES.registerPendingSubscription);
  const isPendingSubscriptionMode = mode === 'pendingSubscription';
  const isSubmitting = isLoading || isRegisterCardPending || isRegisterPendingSubscriptionPending;

  // 카드번호: 숫자만, 국제 카드 대응을 위해 최대 19자리
  const handleCardNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, '').slice(0, MAX_CARD_NUMBER_LENGTH);
    setCardNumber(onlyNums);
  };

  // 유효기간: MM/YY 형식, 숫자만 4자리 입력 후 MM/YY로 변환
  const handleCardExpiry = (e: React.ChangeEvent<HTMLInputElement>) => {
    let onlyNums = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
    if (onlyNums.length >= 3) {
      onlyNums = onlyNums.slice(0, 2) + '/' + onlyNums.slice(2);
    }
    setCardExpiry(onlyNums);
  };

  // 비밀번호 앞 2자리: 숫자만, 최대 2자리, type="password"
  const handleCardPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
    setCardPassword(onlyNums);
  };

  // 생년월일/사업자번호: 숫자만, 최대 10자리
  const handleCardBirth = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
    setCardBirth(onlyNums);
  };

  // MM/YY → YYMM 변환 (예: 08/26 → 2608)
  const getExpiryYYMM = (mmYY: string) => {
    const [mm, yy] = mmYY.split('/');
    if (!mm || !yy) return '';
    return yy + mm;
  };

  const handleCardPay = () => {
    // 유효성 검사 (간단 예시, 필요에 따라 보강)
    if (cardNumber.length < MIN_CARD_NUMBER_LENGTH || cardNumber.length > MAX_CARD_NUMBER_LENGTH) {
      showToast('warning', '카드번호 13~19자리를 입력해주세요.');
      return;
    }
    if (!/^\d{2}\/\d{2}$/.test(cardExpiry)) {
      showToast('warning', '유효기간을 MM/YY 형식으로 입력해주세요.');
      return;
    }
    if (cardPassword.length !== 2) {
      showToast('warning', '카드 비밀번호 앞 2자리를 입력해주세요.');
      return;
    }
    if (cardBirth.length !== 6 && cardBirth.length !== 10) {
      showToast('warning', '생년월일 6자리 또는 사업자번호 10자리를 입력해주세요.');
      return;
    }
    if (!CplatToken) {
      showToast('warning', '로그인이 필요합니다.');
      return;
    }

    if (isPendingSubscriptionMode) {
      registerCardForBilling(
        {
          cplatToken: CplatToken,
          paymentMethod: '일반카드',
          cardNo: cardNumber,
          expirationDate: getExpiryYYMM(cardExpiry),
          password: cardPassword,
          cardOwnerInfo: cardBirth,
        },
        {
          onSuccess: (response) => {
            const responseCode = getCplatCode(response);

            if (responseCode !== '200') {
              showToast(
                'warning',
                getCplatMessage(response) ||
                  '결제수단 등록에 실패했습니다. 입력 정보를 확인하고 다시 시도해 주세요.',
              );
              return;
            }

            requestPendingSubscription(
              createPendingSubscriptionPayload({
                cplatToken: CplatToken,
                channel: pendingSubscriptionChannel ?? WEB_PC_MEMBERSHIP_CHANNEL,
              }),
              {
                onSuccess: (pendingResponse) => {
                  const pendingResponseCode = getCplatCode(pendingResponse);

                  if (pendingResponseCode !== '200') {
                    showToast(
                      'warning',
                      getRegisterPendingSubscriptionErrorMessage(
                        pendingResponseCode,
                        getCplatMessage(pendingResponse),
                      ),
                    );
                    return;
                  }

                  setBillDialogOpen(false);
                  onPendingSubscriptionSuccess?.();
                },
                onError: () => {
                  showToast('warning', '웹 멤버십 예약 신청에 실패했습니다. 다시 시도해 주세요.');
                },
              },
            );
          },
          onError: () => {
            showToast(
              'warning',
              '결제수단 등록에 실패했습니다. 입력 정보를 확인하고 다시 시도해 주세요.',
            );
          },
        },
      );
      return;
    }

    requestCardPay(
      {
        cplatToken: CplatToken,
        subscriptionNo: subscriptionNo, // 실제 상품 번호로 대체
        isPrePayment,
        cardNo: cardNumber,
        expirationDate: getExpiryYYMM(cardExpiry), // YYMM
        password: cardPassword,
        cardOwnerInfo: cardBirth,
      },
      {
        onSuccess: (res) => {
          if (res.data.code === '200') {
            const paymentResultId = getPaymentResultIdFromResponse(res);

            setBillDialogOpen(false); // 다이얼로그 닫기
            navigate(
              createMembershipPaymentResultPath({
                path: ROUTES.moreMembership,
                result: 'success',
                paymentChange,
                paymentResultId,
              }),
            );
          } else {
            showToast('warning', res.data.message || '결제에 실패했습니다. 다시 시도해주세요.');
          }
        },
        onError: () => {
          showToast('warning', '결제에 실패했습니다. 다시 시도해주세요.');
        },
      },
    );
  };

  return (
    <div className="flex flex-col ">
      <ItemLayout>
        <Label>카드번호</Label>
        <div className="w-full">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={MAX_FORMATTED_CARD_NUMBER_LENGTH}
            placeholder="숫자만 입력해 주세요"
            className={CARD_INPUT_CLASS}
            value={cardNumber.replace(/(.{4})/g, '$1 ').trim()} // 4자리마다 공백
            onChange={handleCardNumber}
            autoComplete="cc-number"
          />
        </div>
      </ItemLayout>
      <div className="mt-[12px] flex gap-5">
        <ItemLayout>
          <Label>유효기간</Label>
          <div className="w-full">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9/]*"
              maxLength={5}
              placeholder="MM/YY"
              className={CARD_INPUT_CLASS}
              value={cardExpiry}
              onChange={handleCardExpiry}
              autoComplete="cc-exp"
            />
          </div>
        </ItemLayout>
        <ItemLayout>
          <Label>비밀번호 앞 2자리</Label>
          <div className="w-full">
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={2}
              placeholder="**"
              className={CARD_INPUT_CLASS}
              value={cardPassword}
              onChange={handleCardPassword}
              autoComplete="off"
            />
          </div>
        </ItemLayout>
      </div>
      <div className="mt-[12px] w-full">
        <ItemLayout>
          <Label>생년월일 6자리(개인) / 사업자번호 10자리(법인)</Label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={10}
            placeholder="숫자만 입력해주세요"
            className={CARD_INPUT_CLASS}
            value={cardBirth}
            onChange={handleCardBirth}
            autoComplete="off"
          />
        </ItemLayout>
      </div>
      <div className="mt-[10px] flex w-full items-center justify-end gap-5">
        <div className="flex flex-col items-end">
          <div className="text-gr-900 Body1S16">
            {isPendingSubscriptionMode ? '결제 예정 금액' : '결제 금액'}{' '}
            {paymentAmount.toLocaleString()}원
          </div>
          {(isPrePayment || isPendingSubscriptionMode) && (
            <div className="mt-[2px] text-gr-500 Caption1M12">
              {isPendingSubscriptionMode
                ? '등록된 결제수단으로 인앱 멤버십 종료 후 자동 결제됩니다'
                : '결제수단 등록 후 멤버십 정책에 따라 결제가 진행됩니다'}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <DialogClose asChild>
            <Button size="small" variant="line" className="h-[38px] shadow-none">
              취소
            </Button>
          </DialogClose>
          <Button
            isLoading={isSubmitting}
            size="small"
            onClick={handleCardPay}
            disabled={isSubmitting}
          >
            다음
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChangeBillCard;

const Label = ({ children }: { children: React.ReactNode }) => {
  return <div className="text-gr-800 Caption1M12">{children}</div>;
};

const ItemLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex w-full flex-1 flex-col items-start justify-start gap-2">{children}</div>
  );
};
